import { type Action, type AnyAction, combineReducers } from 'redux';

import type { GroundhogAnalysisResponse, LanguageEnum } from 'js/src/client';
import {
    addIdeaOutlineReference,
    clearGroundhogAnalysisForText,
    deleteIdeaOutlineReference,
    removeScriptFromCache,
    resetScriptCache,
    setCurrentAIAvatar,
    setCurrentAIAvatarVoicePreset,
    setCurrentAIVoice,
    setDocToScriptData,
    setDocToScriptImportType,
    setDocToScriptOptions,
    setDocToScriptPage,
    setEditedScript,
    setEditedScriptInitialWordCount,
    setEditedScriptLanguage,
    setEnforceBlockSegmentation,
    setGroundhogAnalysis,
    setIdeaOutlineReference,
    setIdeaOutlineSubject,
    setIsConvertingToVoiceover,
    setIsFetchingAIScriptOptions,
    setIsFetchingScriptFromIdea,
    setIsFetchingScriptOptionAIScript,
    setIsSegmentationPopoverOpen,
    setIsUploadingScriptImages,
    setLongestSupportedScriptDuration,
    setScriptForScriptOption,
    setSelectedBlockText,
    setSelectedScriptOption,
    setTargetDuration,
    updateDocToScriptOption,
} from 'js/src/data/script-composer/script-composer-actions';
import type {
    DOC_TO_SCRIPT_PAGE,
    DocToScriptData,
    ReadonlyIdeaOutlineValueSet,
    Script,
    ScriptOption,
} from 'js/src/data/script-composer/script-composer-types';
import { DEFAULT_IDEA_OUTLINE_VALUES } from 'js/src/data/script-composer/script-composer-utils';
import { COOKIE_KEYS, type SCRIPT_CREATION_SOURCES } from 'js/src/libs/consts';
import Cookies from 'js-cookie';

export function selectedScriptOption(state: ScriptOption | null = null, action: AnyAction) {
    if (setSelectedScriptOption.match(action)) {
        return action.payload.scriptOption;
    }
    return state;
}

export function docToScriptPage(state: DOC_TO_SCRIPT_PAGE | null = null, action: AnyAction) {
    if (setDocToScriptPage.match(action)) {
        return action.payload.page;
    }
    return state;
}

export function docToScriptData(
    state: DocToScriptData = {
        url: '',
        originalEditorState: undefined,
        shouldSkipAISummarization: undefined,
    },
    action: AnyAction,
) {
    if (setDocToScriptData.match(action)) {
        return Object.assign({}, state, action.payload);
    }
    return state;
}

export function docToScriptImportType(
    state: SCRIPT_CREATION_SOURCES | null = null,
    action: Action,
) {
    if (setDocToScriptImportType.match(action)) {
        return action.payload;
    }
    return state;
}

const NO_SCRIPTS: Readonly<Record<string, Script>> = {};
const NO_SCRIPT_OPTIONS: Readonly<Record<string, readonly ScriptOption[]>> = {};
const NOTHING_FETCHING: Readonly<Record<string, boolean>> = {};

const hasKey = (record: Readonly<Record<string, unknown>>, key: string) =>
    Object.prototype.hasOwnProperty.call(record, key);

const withFlag = (
    state: Readonly<Record<string, boolean>>,
    key: string,
    value: boolean,
): Readonly<Record<string, boolean>> =>
    hasKey(state, key) && state[key] === value ? state : { ...state, [key]: value };

export function scriptCache(
    state: Readonly<Record<string, Script>> = NO_SCRIPTS,
    action: AnyAction,
): Readonly<Record<string, Script>> {
    // data structure is a map of scriptOptionId to Scripts
    if (setScriptForScriptOption.match(action)) {
        const { scriptOptionId, scriptData } = action.payload;
        if (hasKey(state, scriptOptionId) && state[scriptOptionId] === scriptData) {
            return state;
        }
        return { ...state, [scriptOptionId]: scriptData };
    }

    if (removeScriptFromCache.match(action)) {
        const { scriptOptionId } = action.payload;
        if (!hasKey(state, scriptOptionId)) {
            return state;
        }
        const { [scriptOptionId]: _removed, ...remaining } = state;
        return remaining;
    }

    if (resetScriptCache.match(action)) {
        return NO_SCRIPTS;
    }
    return state;
}

export function editedScript(state: Script | null = null, action: AnyAction) {
    if (setEditedScript.match(action)) {
        return action.payload.scriptData;
    }
    return state;
}

export function editedScriptInitialWordCount(state = 0, action: AnyAction) {
    if (setEditedScriptInitialWordCount.match(action)) {
        return action.payload.editedScriptInitialWordCount;
    }
    return state;
}

export function editedScriptLanguage(state: LanguageEnum | null = null, action: AnyAction) {
    if (setEditedScriptLanguage.match(action)) {
        return action.payload;
    }
    return state;
}

export function docToScriptOptions(
    state: Readonly<Record<string, readonly ScriptOption[]>> = NO_SCRIPT_OPTIONS,
    action: AnyAction,
): Readonly<Record<string, readonly ScriptOption[]>> {
    // data structure is a map of scriptId to ScriptOption[]
    if (setDocToScriptOptions.match(action)) {
        // update RawScriptOption to ScriptOption by adding a uuid for each script option
        // uuid = scriptId + index (so it's consistent when running multiple times)
        const scriptOptions = action.payload.scriptOptions.map((scriptOption, index) => {
            return {
                tone: '',
                ...scriptOption,
                uuid: `${action.payload.scriptId}-${index}`,
            };
        });
        return { ...state, [action.payload.scriptId]: scriptOptions };
    }
    if (updateDocToScriptOption.match(action)) {
        const scriptOptionList = hasKey(state, action.payload.scriptId)
            ? state[action.payload.scriptId]
            : undefined;
        if (scriptOptionList) {
            const newScriptOptionList = scriptOptionList.map((scriptOption) =>
                scriptOption.uuid === action.payload.scriptOption.uuid
                    ? action.payload.scriptOption
                    : scriptOption,
            );
            return { ...state, [action.payload.scriptId]: newScriptOptionList };
        }
    }
    return state;
}

export function isFetchingScriptOptionAIScript(
    state: Readonly<Record<string, boolean>> = NOTHING_FETCHING,
    action: AnyAction,
): Readonly<Record<string, boolean>> {
    if (setIsFetchingScriptOptionAIScript.match(action)) {
        return withFlag(state, action.payload.scriptOptionId, action.payload.isLoading);
    }
    return state;
}

export function longestSupportedScriptDuration(state: number | null = null, action: AnyAction) {
    if (setLongestSupportedScriptDuration.match(action)) {
        return action.payload;
    }
    return state;
}

export function isFetchingAIScriptOptions(
    state: Readonly<Record<string, boolean>> = NOTHING_FETCHING,
    action: AnyAction,
): Readonly<Record<string, boolean>> {
    if (setIsFetchingAIScriptOptions.match(action)) {
        return withFlag(state, action.payload.scriptId, action.payload.isLoading);
    }
    return state;
}

export function isFetchingScriptFromIdea(state = false, action: Action) {
    if (setIsFetchingScriptFromIdea.match(action)) {
        return action.payload;
    }
    return state;
}

export function currentAIAvatarVoicePreset(state: number | null = null, action: Action) {
    if (setCurrentAIAvatarVoicePreset.match(action)) {
        return action.payload;
    }
    return state;
}

export function currentAIAvatar(state: number | null = null, action: Action) {
    if (setCurrentAIAvatar.match(action)) {
        return action.payload;
    }
    return state;
}

export function currentAIVoice(state: string | null = null, action: Action) {
    if (setCurrentAIVoice.match(action)) {
        return action.payload;
    }
    return state;
}

export function ideaOutlineData(
    state: ReadonlyIdeaOutlineValueSet = DEFAULT_IDEA_OUTLINE_VALUES,
    action: Action,
): ReadonlyIdeaOutlineValueSet {
    if (setIdeaOutlineSubject.match(action)) {
        return state.subject === action.payload ? state : { ...state, subject: action.payload };
    } else if (setIdeaOutlineReference.match(action)) {
        const { index, reference } = action.payload;
        const references = [...state.references];
        references[index] = reference;
        return { ...state, references };
    } else if (addIdeaOutlineReference.match(action)) {
        return { ...state, references: [...state.references, ''] };
    } else if (deleteIdeaOutlineReference.match(action)) {
        if (action.payload < 0 || action.payload >= state.references.length) {
            return state;
        }
        return {
            ...state,
            references: state.references.filter((_, index) => index !== action.payload),
        };
    }
    return state;
}

export function isUploadingScriptImages(state = false, action: Action) {
    if (setIsUploadingScriptImages.match(action)) {
        return action.payload;
    }
    return state;
}

export function targetDuration(state: number | null = null, action: Action) {
    if (setTargetDuration.match(action)) {
        return action.payload;
    }
    return state;
}

export function groundhogAnalysis(state: GroundhogAnalysisResponse | null = null, action: Action) {
    if (setGroundhogAnalysis.match(action)) {
        return action.payload;
    }
    if (clearGroundhogAnalysisForText.match(action)) {
        if (state === null) {
            return null;
        }
        // filter out any sentences that match the provided text
        const filteredAnalysis = state.script_analysis.filter(
            (analysis) => analysis.sentence_text !== action.payload.text,
        );
        return {
            ...state,
            script_analysis: filteredAnalysis,
        };
    }
    return state;
}

export function selectedBlockText(state: string | null = null, action: AnyAction): string | null {
    if (setSelectedBlockText.match(action)) {
        return action.payload;
    }
    return state;
}

export function isConvertingToVoiceover(state: boolean = false, action: AnyAction): boolean {
    if (setIsConvertingToVoiceover.match(action)) {
        return action.payload;
    }
    return state;
}

const enforceBlockSegmentationInitialState = (): boolean =>
    Cookies.get(COOKIE_KEYS.ALWAYS_ENFORCE_BLOCK_SEGMENTATION) === 'true';

export function enforceBlockSegmentation(
    state: boolean = enforceBlockSegmentationInitialState(),
    action: AnyAction,
): boolean {
    if (setEnforceBlockSegmentation.match(action)) {
        return action.payload;
    }
    return state;
}

export function isSegmentationPopoverOpen(state: boolean = false, action: AnyAction): boolean {
    if (setIsSegmentationPopoverOpen.match(action)) {
        return action.payload;
    }
    return state;
}

export const script_composer = combineReducers({
    docToScriptPage,
    docToScriptData,
    docToScriptImportType,
    scriptCache,
    docToScriptOptions,
    isFetchingScriptOptionAIScript,
    isFetchingAIScriptOptions,
    isFetchingScriptFromIdea,
    isUploadingScriptImages,
    selectedScriptOption,
    longestSupportedScriptDuration,
    editedScript,
    editedScriptInitialWordCount,
    editedScriptLanguage,
    currentAIAvatarVoicePreset,
    currentAIAvatar,
    currentAIVoice,
    ideaOutlineData,
    targetDuration,
    groundhogAnalysis,
    selectedBlockText,
    isConvertingToVoiceover,
    enforceBlockSegmentation,
    isSegmentationPopoverOpen,
});

export type ScriptComposerState = ReturnType<typeof script_composer>;
